/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { MonitorAgent } from "@/api/monitor";
import { getMonitorAgentDetail, SystemDetail } from "@/api/serverDetail";

export const SERVER_DETAIL_INTERVAL_MS = 5000;
const HISTORY_LENGTH = 120; // ~10 minutes at 5 s

export interface DetailRates {
  nics: Record<string, { rx: number; tx: number }>; // bytes/s
  disks: Record<string, { read: number; write: number; busy: number }>; // bytes/s, % busy
  netRx: number;
  netTx: number;
  diskRead: number;
  diskWrite: number;
}

export interface HistoryPoint {
  t: number;
  cpu: number;
  iowait: number;
  rx: number;
  tx: number;
  read: number;
  write: number;
}

const emptyRates: DetailRates = {
  nics: {},
  disks: {},
  netRx: 0,
  netTx: 0,
  diskRead: 0,
  diskWrite: 0,
};

// Counters that must not be double counted when summing "all interfaces".
const VIRTUAL_NIC = /^(lo|veth|docker|br-|tap|fw|virbr|wt|tailscale|ts)/;

function computeRates(prev: SystemDetail, next: SystemDetail): DetailRates | null {
  const dt = (Date.parse(next.collected_at) - Date.parse(prev.collected_at)) / 1000;
  if (!(dt > 0.5)) return null;
  const out: DetailRates = { nics: {}, disks: {}, netRx: 0, netTx: 0, diskRead: 0, diskWrite: 0 };

  const prevNics = new Map((prev.nics ?? []).map((n) => [n.name, n]));
  for (const n of next.nics ?? []) {
    const p = prevNics.get(n.name);
    if (!p) continue;
    const rx = Math.max(0, (n.rx_bytes - p.rx_bytes) / dt);
    const tx = Math.max(0, (n.tx_bytes - p.tx_bytes) / dt);
    out.nics[n.name] = { rx, tx };
    if (!VIRTUAL_NIC.test(n.name) && !n.name.startsWith("vmbr")) {
      out.netRx += rx;
      out.netTx += tx;
    }
  }
  // Bridges (vmbr*) carry the same traffic as their physical port; only count
  // them when the host has no non-virtual NIC with counters at all.
  if (out.netRx === 0 && out.netTx === 0) {
    for (const [name, r] of Object.entries(out.nics)) {
      if (name.startsWith("vmbr")) {
        out.netRx += r.rx;
        out.netTx += r.tx;
      }
    }
  }

  const prevDisks = new Map((prev.disk_io ?? []).map((d) => [d.name, d]));
  for (const d of next.disk_io ?? []) {
    const p = prevDisks.get(d.name);
    if (!p) continue;
    const read = Math.max(0, (d.read_bytes - p.read_bytes) / dt);
    const write = Math.max(0, (d.write_bytes - p.write_bytes) / dt);
    const busy = Math.min(100, Math.max(0, ((d.io_time_ms - p.io_time_ms) / (dt * 1000)) * 100));
    out.disks[d.name] = { read, write, busy };
    out.diskRead += read;
    out.diskWrite += write;
  }
  return out;
}

export function useServerDetail(agent: MonitorAgent) {
  const query = useQuery<SystemDetail>({
    queryKey: ["server-detail", agent.id],
    queryFn: () => getMonitorAgentDetail(agent.id),
    refetchInterval: agent.enabled ? SERVER_DETAIL_INTERVAL_MS : false,
    enabled: agent.enabled,
    retry: false,
    staleTime: SERVER_DETAIL_INTERVAL_MS / 2,
  });

  const [rates, setRates] = useState<DetailRates>(emptyRates);
  const [history, setHistory] = useState<HistoryPoint[]>([]);
  const prev = useRef<SystemDetail | null>(null);

  // A different server starts with a clean slate.
  useEffect(() => {
    prev.current = null;
    setRates(emptyRates);
    setHistory([]);
  }, [agent.id]);

  useEffect(() => {
    const data = query.data;
    if (!data) return;
    const before = prev.current;
    if (before && before.collected_at === data.collected_at) return;
    prev.current = data;

    const r = before ? computeRates(before, data) : null;
    if (r) setRates(r);
    setHistory((h) => {
      const point: HistoryPoint = {
        t: Date.parse(data.collected_at),
        cpu: data.cpu.total,
        iowait: data.cpu.iowait,
        rx: r?.netRx ?? h[h.length - 1]?.rx ?? 0,
        tx: r?.netTx ?? h[h.length - 1]?.tx ?? 0,
        read: r?.diskRead ?? h[h.length - 1]?.read ?? 0,
        write: r?.diskWrite ?? h[h.length - 1]?.write ?? 0,
      };
      const next = [...h, point];
      return next.length > HISTORY_LENGTH ? next.slice(next.length - HISTORY_LENGTH) : next;
    });
  }, [query.dataUpdatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    detail: query.data,
    error: query.error as Error | null,
    isLoading: query.isLoading,
    isError: query.isError,
    dataUpdatedAt: query.dataUpdatedAt,
    rates,
    history,
  };
}
